import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "../lib/api";
import { Gavel, Clock, Flame, Bell, ArrowRight, X, Zap, ShieldCheck, Wallet, Shield } from "lucide-react";
import { formatIDR } from "../lib/format";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PinModal from "../components/PinModal";
import { useWallet } from "../context/WalletContext";

const BACKEND = API.replace(/\/api$/, "");
const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND}${url}`;
};

const SUB_CATEGORY_LABELS = {
  akun: "Akun",
  topup_via_id: "Top-up via ID",
  topup_via_login: "Top-up via Login",
  voucher: "Voucher",
};

const GARANSI_LABELS = {
  akun: "Garansi 7 hari",
  topup_via_id: "Garansi 1 hari",
  topup_via_login: "Garansi 7 hari",
  voucher: "Garansi 6 jam",
};

function useCountdown(endsAt, isFrozen = false) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (isFrozen) return { text: "FROZEN", ended: false };
  const remaining = Math.max(0, new Date(endsAt).getTime() - now);
  const s = Math.floor(remaining / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const text =
    h >= 24
      ? `${Math.floor(h / 24)}h ${h % 24}j ${m}m`
      : `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return { text, ended: remaining <= 0 };
}

function AuctionCountdown({ endsAt, isFrozen }) {
  const { text, ended } = useCountdown(endsAt, isFrozen);
  return (
    <span className={`font-mono ${isFrozen ? "text-[#7C6EFF]" : ended ? "text-[#FF003C]" : "text-[#FFD700]"}`}>
      {ended ? "ENDED" : text}
    </span>
  );
}

function BidModal({ auction, onClose, onBidPlaced }) {
  const wallet = useWallet();
  const { user, refresh: refreshUser } = useAuth();
  const navigate = useNavigate();
  const minBid = auction.min_next_bid || auction.starting_bid;
  const [amount, setAmount] = useState(minBid);
  const [busy, setBusy] = useState(false);
  const [pinMode, setPinMode] = useState(null);
  const [pendingBid, setPendingBid] = useState(null); // {amount, isBin}

  const myCurrentLock = auction.current_bid_user_id === wallet.user_id ? (auction.current_bid_amount || 0) : 0;
  const effectiveBalance = (wallet.balance || 0) + myCurrentLock;
  const insufficient = amount > effectiveBalance;

  const doBid = async (pin, finalAmount, isBin) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/auctions/${auction.id}/bid`, { amount: finalAmount, pin });
      wallet.refresh();
      onBidPlaced(data.auction);
      onClose();
      if (data.bin_triggered) {
        toast.success(`🎉 BUY NOW BERHASIL! Kamu menang ${auction.title} dengan ${formatIDR(finalAmount)}`, { duration: 6000 });
        navigate(`/auctions/${auction.id}`);
      } else {
        toast.success(`Bid ${formatIDR(finalAmount)} berhasil! Saldo dikunci.`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Bid failed";
      if (msg.toLowerCase().includes("saldo")) {
        const top = window.confirm(`${msg}\n\nTop-up saldo sekarang?`);
        if (top) navigate("/wallet");
      } else { toast.error(msg); }
    } finally { setBusy(false); }
  };

  const submit = (e, useBin = false) => {
    e?.preventDefault();
    const finalAmount = useBin ? auction.buy_now_price : parseFloat(amount);
    if (finalAmount > effectiveBalance) {
      const top = window.confirm("Saldo kamu tidak cukup. Top-up sekarang?");
      if (top) navigate("/wallet");
      return;
    }
    setPendingBid({ amount: finalAmount, isBin: useBin });
    setPinMode(user?.has_pin ? "verify" : "set");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="bid-modal">
      <div className="relative w-full max-w-md border border-[#7C6EFF]/40 bg-[#0a0a0a] p-6 max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-white/60 hover:text-white">
          <X className="w-5 h-5" />
        </button>
        <span className="tag" style={{ borderColor: "rgba(124,110,255,0.6)", color: "#7C6EFF", background: "rgba(124,110,255,0.1)" }}>
          PLACE BID
        </span>
        <h2 className="font-display font-black text-2xl text-white mt-3 leading-tight">{auction.title}</h2>
        <div className="mt-2 text-xs text-white/50">{auction.game_name} · {SUB_CATEGORY_LABELS[auction.sub_category]}</div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/40">Current bid</p>
            <p className="font-display font-bold text-lg text-[#00F0FF]">
              {auction.current_bid_amount ? formatIDR(auction.current_bid_amount) : "—"}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-white/40">Min next bid</p>
            <p className="font-display font-bold text-lg text-[#FFD700]">{formatIDR(minBid)}</p>
          </div>
        </div>
        <div className="mt-4 p-3 border border-[#00F0FF]/20 bg-[#00F0FF]/5">
          <div className="flex items-center gap-2 text-xs">
            <Wallet className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-white/60 uppercase tracking-widest">Saldo:</span>
            <span className="font-bold text-[#00F0FF] ml-auto" data-testid="bid-wallet-balance">{formatIDR(wallet.balance || 0)}</span>
          </div>
        </div>
        <form onSubmit={submit} className="mt-4" data-testid="bid-form">
          <label className="text-xs uppercase tracking-[0.2em] text-white/60">Your bid (IDR)</label>
          <input
            data-testid="bid-amount-input"
            type="number"
            min={minBid}
            step={auction.bid_increment || 1000}
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-black border border-white/15 focus:border-[#7C6EFF] text-white text-lg font-display font-bold px-4 py-3 mt-2"
          />
          <p className="text-[10px] text-white/40 mt-1">{formatIDR(amount || 0)}</p>
          {(() => {
            const step = auction.bid_increment || 5000;
            const mults = auction.bid_increment ? [1, 2, 5, 10] : [1, 2, 5, 10];
            return (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="bid-quick-add">
                {mults.map((m) => {
                  const inc = step * m;
                  return (
                    <button
                      key={m}
                      type="button"
                      data-testid={`bid-add-${inc}`}
                      onClick={() => setAmount((prev) => Math.max(minBid, (parseFloat(prev) || minBid) + inc))}
                      className="text-[10px] px-3 py-1.5 border border-white/15 hover:border-[#00F0FF] text-white/70 hover:text-[#00F0FF] uppercase tracking-widest"
                    >
                      +{formatIDR(inc).replace("Rp ", "")}
                    </button>
                  );
                })}
                <button type="button" data-testid="bid-set-min" onClick={() => setAmount(minBid)} className="text-[10px] px-3 py-1.5 border border-[#FFD700]/40 hover:border-[#FFD700] text-[#FFD700] uppercase tracking-widest">
                  Min {formatIDR(minBid).replace("Rp ", "")}
                </button>
              </div>
            );
          })()}
          {insufficient && (
            <p className="text-[#FF003C] text-xs mt-2" data-testid="insufficient-warning">
              ⚠ Saldo kurang {formatIDR(amount - effectiveBalance)}. <Link to="/wallet" className="underline">Top-up dulu</Link>.
            </p>
          )}
          <button
            data-testid="bid-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full mt-4 inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#3D2BFF] hover:bg-[#5C4DFF] text-white font-display font-black uppercase tracking-[0.15em] text-xs disabled:opacity-50"
          >
            <Gavel className="w-4 h-4" /> {busy ? "Placing..." : "Place Bid"}
          </button>
          {auction.buy_now_price && (
            <button
              data-testid="bid-bin-btn"
              type="button"
              onClick={(e) => submit(e, true)}
              disabled={busy}
              className="w-full mt-2 inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#FFD700]/60 text-[#FFD700] hover:bg-[#FFD700]/10 font-display font-black uppercase tracking-[0.15em] text-xs disabled:opacity-50"
            >
              <Zap className="w-4 h-4" /> Buy Now {formatIDR(auction.buy_now_price)}
            </button>
          )}
          <p className="text-[10px] uppercase tracking-widest text-white/30 mt-3 text-center">
            Saldo dikunci. Refund otomatis kalau kalah.
          </p>
        </form>
      </div>
      {pinMode && pendingBid && (
        <PinModal
          mode={pinMode}
          isChange={false}
          title={pendingBid.isBin ? "Konfirmasi Buy Now" : "Konfirmasi Bid"}
          description={`${pendingBid.isBin ? "Buy Now" : "Bid"} ${formatIDR(pendingBid.amount)} untuk ${auction.title}`}
          onClose={() => { setPinMode(null); setPendingBid(null); }}
          onSuccess={(pin) => { refreshUser?.(); doBid(pin, pendingBid.amount, pendingBid.isBin); }}
        />
      )}
    </div>
  );
}

function StatusBadge({ status, isFrozen }) {
  const map = {
    live: { c: "rgba(0,240,255,0.4)", b: "rgba(0,240,255,0.1)", t: "#00F0FF", label: "LIVE" },
    frozen: { c: "rgba(124,110,255,0.6)", b: "rgba(124,110,255,0.15)", t: "#7C6EFF", label: "FROZEN (di luar jam aktif)" },
    ended: { c: "rgba(255,215,0,0.5)", b: "rgba(255,215,0,0.1)", t: "#FFD700", label: "ENDED" },
    sold: { c: "rgba(0,255,102,0.5)", b: "rgba(0,255,102,0.1)", t: "#00FF66", label: "SOLD" },
    expired: { c: "rgba(255,255,255,0.3)", b: "rgba(255,255,255,0.05)", t: "rgba(255,255,255,0.6)", label: "EXPIRED" },
  };
  const m = map[status] || map.live;
  return <span className="tag" style={{ borderColor: m.c, background: m.b, color: m.t }}>{m.label}</span>;
}

export default function Auction() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidAuction, setBidAuction] = useState(null);
  const [subFilter, setSubFilter] = useState("");

  const refresh = useCallback(async () => {
    const url = subFilter ? `/auctions?sub_category=${subFilter}` : "/auctions";
    const { data } = await api.get(url);
    // Only show live/frozen
    setAuctions(data.filter((a) => ["live", "frozen"].includes(a.status)));
  }, [subFilter]);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [refresh]);

  const openBid = async (a) => {
    if (!user) return navigate("/login");
    try {
      const { data } = await api.get(`/auctions/${a.id}`);
      setBidAuction(data);
    } catch {
      setBidAuction(a);
    }
  };

  return (
    <div data-testid="auction-page" className="min-h-screen">
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-50" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 70% 30%, rgba(61, 43, 255, 0.25) 0%, transparent 60%), linear-gradient(180deg, rgba(5,5,5,0.4) 0%, #050505 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="flex items-center gap-2 mb-6">
            <span className="tag" style={{ borderColor: "rgba(61,43,255,0.6)", color: "#7C6EFF", background: "rgba(61,43,255,0.1)" }}>// ARENA · LIVE</span>
            <span className="tag tag-accent">{auctions.length} ACTIVE</span>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.95] text-white">
            Bid like a <span style={{ color: "#7C6EFF", textShadow: "0 0 24px rgba(124,110,255,0.5)" }}>predator.</span>
          </h1>
          <p className="mt-6 text-white/70 text-base md:text-lg max-w-2xl leading-relaxed">
            Lelang akun langka, voucher premium, dan top-up eksklusif. Bayar dengan saldo Ustore — anti-snipe 2 menit · jam aktif 08:00-21:00 WIB.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#live-auctions" className="inline-flex items-center gap-2 px-6 py-3 bg-[#3D2BFF] hover:bg-[#5C4DFF] text-white font-display font-black uppercase tracking-[0.15em] text-xs" style={{ boxShadow: "0 0 24px rgba(61,43,255,0.4)" }}>
              <Gavel className="w-4 h-4" /> Lihat Live
            </a>
            <Link to="/wallet" data-testid="nav-to-wallet-btn" className="btn-ghost inline-flex items-center gap-2">
              <Wallet className="w-4 h-4" /> Top-up Saldo
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-4 gap-4 max-w-2xl">
            {[
              { n: `${auctions.length}`, l: "Live" },
              { n: "2m", l: "Anti-Snipe" },
              { n: "08-21", l: "Jam Aktif WIB" },
              { n: "100%", l: "Wallet" },
            ].map((stat) => (
              <div key={stat.l} className="border-l-2 border-[#7C6EFF] pl-3">
                <div className="font-display font-black text-xl text-white">{stat.n}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{stat.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="live-auctions" className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <span className="tag tag-accent">// HOT LOTS</span>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white mt-2">Auction Aktif</h2>
          </div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FFD700]">
            <Flame className="w-4 h-4" />
            Real-time
          </div>
        </div>

        {/* Sub-category filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button data-testid="sub-filter-all" onClick={() => setSubFilter("")} className={`px-3 py-2 text-xs uppercase tracking-widest border ${!subFilter ? "border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-white/60 hover:border-white/30"}`}>Semua</button>
          {Object.entries(SUB_CATEGORY_LABELS).map(([k, v]) => (
            <button key={k} data-testid={`sub-filter-${k}`} onClick={() => setSubFilter(k)} className={`px-3 py-2 text-xs uppercase tracking-widest border ${subFilter === k ? "border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-white/60 hover:border-white/30"}`}>
              {v}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-white/50 text-center py-20">Loading auctions...</p>
        ) : auctions.length === 0 ? (
          <div data-testid="no-auctions" className="border border-white/10 p-12 text-center text-white/60">Belum ada auction live.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {auctions.map((a) => (
              <div key={a.id} data-testid={`auction-card-${a.id}`} className="border border-white/10 hover:border-[#7C6EFF]/60 bg-[#0a0a0a] transition-all duration-200 hover:-translate-y-1 flex flex-col">
                <Link to={`/auctions/${a.id}`} data-testid={`auction-card-link-${a.id}`} className="block">
                <div className="aspect-[16/10] bg-black overflow-hidden relative">
                  {a.image_url && <img src={resolveImg(a.image_url)} alt={a.title} className="w-full h-full object-cover" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />

                  {/* Top-left badges */}
                  <div className="absolute top-3 left-3 flex items-center gap-2 flex-wrap">
                    <StatusBadge status={a.status} />
                    {a.is_admin && (
                      <span data-testid={`admin-badge-${a.id}`} className="tag" style={{ borderColor: "rgba(255,0,60,0.6)", color: "#FF003C", background: "rgba(255,0,60,0.1)" }}>
                        ADMIN
                      </span>
                    )}
                    {a.is_instant && (
                      <span data-testid={`instant-badge-${a.id}`} className="tag" style={{ borderColor: "rgba(0,255,102,0.6)", color: "#00FF66", background: "rgba(0,255,102,0.1)" }}>
                        ⚡ INSTANT DELIVERY
                      </span>
                    )}
                  </div>

                  {/* Top-right: countdown */}
                  <div className="absolute top-3 right-3">
                    <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/80 bg-black/60 px-2 py-1 border border-white/15">
                      <Clock className="w-3 h-3 text-[#FFD700]" /> <AuctionCountdown endsAt={a.ends_at} isFrozen={a.status === "frozen"} />
                    </span>
                  </div>

                  {/* Bottom-left: game name */}
                  <div className="absolute bottom-3 left-3">
                    <span className="tag tag-accent">{a.game_name}</span>
                  </div>

                  {/* BIN banner — bottom-right */}
                  {a.buy_now_price && (
                    <div className="absolute bottom-3 right-3" data-testid={`bin-banner-${a.id}`}>
                      <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-black bg-[#FFD700] px-2 py-1 font-bold">
                        <Zap className="w-3 h-3" /> BIN {formatIDR(a.buy_now_price)}
                      </span>
                    </div>
                  )}
                </div>
                </Link>

                <div className="p-4 flex-1 flex flex-col">
                  <Link to={`/auctions/${a.id}`} className="block">
                  <h3 className="font-bold text-white text-base line-clamp-2 min-h-[48px] hover:text-[#00F0FF]">{a.title}</h3>
                  </Link>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-xs text-white/50">{a.seller_name}</p>
                    <span className="text-white/30">·</span>
                    <p className="text-[10px] uppercase tracking-widest text-white/50">{SUB_CATEGORY_LABELS[a.sub_category]}</p>
                  </div>
                  <div className="mt-3 flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#00FF66]">
                    <Shield className="w-3 h-3" /> {GARANSI_LABELS[a.sub_category]}
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 pb-4 border-b border-white/10">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{a.current_bid_amount ? "Current bid" : "Start at"}</div>
                      <div className="font-display font-black text-lg text-[#00F0FF]">{formatIDR(a.current_bid_amount || a.starting_bid)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Bids</div>
                      <div className="font-display font-black text-lg text-white">{a.bid_count}</div>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Link
                      to={`/auctions/${a.id}`}
                      data-testid={`detail-btn-${a.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-3 border border-white/20 hover:border-[#00F0FF] text-white/80 hover:text-[#00F0FF] font-display font-black uppercase tracking-[0.15em] text-[11px]"
                    >
                      Detail
                    </Link>
                    <button
                      data-testid={`bid-btn-${a.id}`}
                      onClick={(e) => { e.preventDefault(); openBid(a); }}
                      disabled={a.status !== "live"}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-3 bg-[#3D2BFF] hover:bg-[#5C4DFF] text-white font-display font-black uppercase tracking-[0.15em] text-[11px] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Gavel className="w-3.5 h-3.5" /> {a.status === "frozen" ? "Frozen" : "Bid"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 border-t border-white/10">
        <span className="tag tag-accent">// PERATURAN ARENA</span>
        <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white mt-2 mb-12">Cara kerja</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: "01", icon: Wallet, t: "Top-up saldo dulu", d: "Semua bid wajib pakai saldo Ustore. Top-up via Tripay (QRIS, GoPay, BCA VA, dll) di /wallet." },
            { n: "02", icon: Gavel, t: "Bid & saldo dikunci", d: "Saldo terkunci selama jadi top bidder. Kalau dikalahkan, otomatis di-refund ke saldo. Bid jam 08:00-21:00 WIB." },
            { n: "03", icon: ShieldCheck, t: "Anti-snipe 2 menit", d: "Kalau ada bid di 30 menit terakhir, timer extend +2 menit. Out-of-hours auction freeze otomatis." },
            { n: "04", icon: Zap, t: "Menang? Chat seller", d: "Otomatis dapat notif email + chat dari seller. Akun instant-delivery dikirim langsung di chat." },
            { n: "05", icon: Shield, t: "Garansi otomatis", d: "Akun & top-up via login: 7 hari · Top-up via ID: 1 hari · Voucher: 6 jam. Saldo cair ke seller setelah garansi habis." },
            { n: "06", icon: Bell, t: "Komplain? Extend garansi", d: "Buyer bisa request perpanjang garansi +7 hari dengan alasan. Tim admin Ustore review dalam 24 jam." },
          ].map((s) => (
            <div key={s.n} className="border border-white/10 p-6">
              <div className="flex items-center gap-3">
                <s.icon className="w-5 h-5 text-[#7C6EFF]" />
                <div className="font-display font-black text-3xl text-[#7C6EFF]">{s.n}</div>
              </div>
              <h3 className="font-display font-bold text-lg text-white mt-2">{s.t}</h3>
              <p className="text-sm text-white/60 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {bidAuction && <BidModal auction={bidAuction} onClose={() => setBidAuction(null)} onBidPlaced={() => refresh()} />}
    </div>
  );
}
